import { ALL_TOURS } from './tours'

/**
 * Whether each page's guide has been shown, as one boolean per page in a single
 * localStorage entry: `{"welcome":true,"menu":false,...}`, keyed by tour id.
 *
 * Keyed rather than positional on purpose. An array indexed by page order broke
 * as soon as the list of pages changed: inserting or reordering shifted every
 * stored flag, replaying guides users had already seen. By name, a page added,
 * removed or reordered cannot disturb another page's flag — an absent key just
 * reads as false, which is exactly what a page nobody has visited should say.
 */
const SEEN_PAGES_KEY = 'onboarding.seenPages'

/** Keys this map replaced. Read once, folded in, then deleted. */
const LEGACY_COMPLETED_KEY = 'onboarding.completed.v1'
const LEGACY_SEEN_PREFIX = 'onboarding.seen.'
const LEGACY_OPTED_OUT_KEY = 'onboarding.optedOut'

type SeenPages = Record<string, boolean>

function legacySeenKey(tour: string) {
  return `${LEGACY_SEEN_PREFIX}${tour}`
}

function parseSeenPages(raw: string | null): SeenPages {
  if (!raw) return {}
  try {
    const value: unknown = JSON.parse(raw)
    // `null` and arrays are both typeof 'object'; neither is a flag map. An
    // array is also what the short-lived positional version wrote, and reading
    // it as "nothing seen" is the harmless way to land: a guide shows once more,
    // rather than a mis-indexed flag hiding one for good.
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return {}
    }
    return value as SeenPages
  } catch {
    // A truncated or hand-edited entry is worth nothing. Starting over shows a
    // guide again, which is the harmless direction to fail in — the other way
    // round, one bad character would hide every guide forever.
    return {}
  }
}

/**
 * One entry per page that exists today. Anything stored for a page that no
 * longer exists is dropped on the next write; anything missing reads as false.
 */
function normalise(pages: SeenPages): SeenPages {
  const flags: SeenPages = {}
  ALL_TOURS.forEach(tour => {
    flags[tour] = pages[tour] === true
  })
  return flags
}

function writeSeenPages(pages: SeenPages) {
  localStorage.setItem(SEEN_PAGES_KEY, JSON.stringify(normalise(pages)))
}

/**
 * Folds the keys the map replaced into it. Idempotent by construction — it
 * detects "nothing left to migrate" from the absence of those keys rather than
 * from a flag — so it can run on every read. That costs a few localStorage
 * lookups per route change and needs no ordering between effects, which an
 * effect-based or import-time migration would.
 */
function migrateLegacyKeys() {
  // The guide no longer has a global "stop offering these" switch: a page's own
  // flag is the only thing that decides whether its guide opens.
  localStorage.removeItem(LEGACY_OPTED_OUT_KEY)

  const completedEverything =
    localStorage.getItem(LEGACY_COMPLETED_KEY) !== null
  const legacyTours = ALL_TOURS.filter(
    tour => localStorage.getItem(legacySeenKey(tour)) !== null
  )
  if (!completedEverything && legacyTours.length === 0) return

  const stored = normalise(parseSeenPages(localStorage.getItem(SEEN_PAGES_KEY)))
  ALL_TOURS.forEach(tour => {
    if (completedEverything || legacyTours.includes(tour)) stored[tour] = true
  })
  writeSeenPages(stored)

  localStorage.removeItem(LEGACY_COMPLETED_KEY)
  ALL_TOURS.forEach(tour => localStorage.removeItem(legacySeenKey(tour)))
}

function readSeenPages(): SeenPages {
  migrateLegacyKeys()
  return normalise(parseSeenPages(localStorage.getItem(SEEN_PAGES_KEY)))
}

export function hasSeenPage(tour: string): boolean {
  return readSeenPages()[tour] === true
}

/**
 * Set when a page's guide opens, not when it finishes: a guide abandoned halfway
 * should not ambush the user again on their next visit. The sidebar's replay
 * button is the way back in, and it ignores these flags entirely.
 */
export function markPageSeen(tour: string) {
  if (!ALL_TOURS.includes(tour)) return
  const flags = readSeenPages()
  if (flags[tour]) return
  flags[tour] = true
  writeSeenPages(flags)
}
